import { fetchQuery, type useRelayEnvironment } from 'react-relay';

import type { StreamState } from './useCommentAgentSession.ts';
import { CommentAgentSessionsQuery } from './CommentAgentSessionsQuery.graphql.ts';
import type { CommentAgentSessionsQuery as SessionsQuery } from './__generated__/CommentAgentSessionsQuery.graphql.ts';
import {
	MAX_SESSION_PAGES,
	SESSION_PAGE_SIZE,
	hasPendingSessionSnapshotRequest,
} from './usePageCommentAgentSessions.ts';

export const COMMENT_SESSION_POLL_INTERVAL_MS = 30_000;

type Environment = ReturnType<typeof useRelayEnvironment>;

type Snapshot = Map<string, StreamState>;
export type PollingFailureReason = 'requestFailed' | 'statusMissing';
// Capture subscription freshness when the request starts, rather than when it finishes.
type Listener = () => (snapshot: Snapshot, failureReason?: PollingFailureReason) => void;
type Poll = {
	listeners: Map<Listener, number>;
	resume: () => void;
	release: () => void;
};
const polls = new WeakMap<Environment, Map<string, Poll>>();

function isSessionState(state: string | null | undefined): state is StreamState {
	return (
		state === 'WORKING' ||
		state === 'INPUT_REQUIRED' ||
		state === 'ARTEFACT_READY' ||
		state === 'FAILED' ||
		state === 'COMPLETED' ||
		state === 'CANCELLED'
	);
}

/** Confirm a completion event against the current session, which can outlive a completed tool. */
export async function fetchCommentAgentSessionState(
	environment: Environment,
	pageId: string,
	conversationId: string,
): Promise<StreamState | undefined> {
	let after: string | null = null;
	for (let page = 0; page < MAX_SESSION_PAGES; page++) {
		const data = await fetchQuery<SessionsQuery>(
			environment,
			CommentAgentSessionsQuery,
			{ pageId, status: ['current', 'draft', 'archived'], first: SESSION_PAGE_SIZE, after },
			{ fetchPolicy: 'network-only' },
		).toPromise();
		const connection = data?.content?.nodes?.find(
			(node) => node?.allCommentAgentSessions,
		)?.allCommentAgentSessions;
		if (!connection) return;
		const session = connection.edges?.find(
			(edge) => edge?.node?.conversationId === conversationId,
		)?.node;
		if (isSessionState(session?.agentSession?.state)) return session.agentSession.state;
		const next = connection.pageInfo?.endCursor;
		if (!connection.pageInfo?.hasNextPage || !next || next === after) return;
		after = next;
	}
}

/** One network loop per page, shared by visible status rows across comment surfaces. */
export function pollCommentAgentSessions(
	environment: Environment,
	pageId: string,
	listener: Listener,
): () => void {
	const browserDocument = typeof document === 'undefined' ? undefined : document;
	const browserNavigator = typeof navigator === 'undefined' ? undefined : navigator;
	if (!browserDocument || !browserNavigator) return () => {};
	let pages = polls.get(environment);
	if (!pages) {
		pages = new Map();
		polls.set(environment, pages);
	}
	let poll = pages.get(pageId);
	if (!poll) {
		const listeners = new Map<Listener, number>();
		let timer: ReturnType<typeof setTimeout> | undefined;
		let inFlight = false;
		let retryDelay = COMMENT_SESSION_POLL_INTERVAL_MS;
		const canPoll = () =>
			listeners.size > 0 && !browserDocument.hidden && browserNavigator.onLine !== false;
		const clearTimer = () => {
			clearTimeout(timer);
			timer = undefined;
		};
		const schedule = () => {
			clearTimer();
			if (canPoll() && !inFlight) timer = setTimeout(() => void refresh(), retryDelay);
		};
		const refresh = async () => {
			timer = undefined;
			if (!canPoll()) return;
			if (hasPendingSessionSnapshotRequest(environment, pageId)) {
				schedule();
				return;
			}
			inFlight = true;
			const receivers = new Map(Array.from(listeners.keys(), (start) => [start, start()]));
			const snapshot: Snapshot = new Map();
			let failureReason: PollingFailureReason | undefined;
			try {
				let after: string | null = null;
				for (let page = 0; page < MAX_SESSION_PAGES && canPoll(); page++) {
					// Later discovery requests can share this request without predating its freshness check.
					const data = await fetchQuery<SessionsQuery>(
						environment,
						CommentAgentSessionsQuery,
						{ pageId, status: ['current', 'draft', 'archived'], first: SESSION_PAGE_SIZE, after },
						{ fetchPolicy: 'network-only' },
					).toPromise();
					const connection = data?.content?.nodes?.find(
						(node) => node?.allCommentAgentSessions,
					)?.allCommentAgentSessions;
					if (!connection) break;
					// At most MAX_SESSION_PAGES pages of SESSION_PAGE_SIZE rows.
					// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
					for (const edge of connection.edges ?? []) {
						const session = edge?.node;
						if (session?.conversationId && isSessionState(session.agentSession?.state)) {
							snapshot.set(session.conversationId, session.agentSession.state);
						}
					}
					const next = connection.pageInfo?.endCursor;
					if (!connection.pageInfo?.hasNextPage || !next || next === after) break;
					after = next;
				}
				retryDelay = COMMENT_SESSION_POLL_INTERVAL_MS;
			} catch {
				// Transport errors leave the current status/proposal intact.
				failureReason = 'requestFailed';
				retryDelay = Math.min(retryDelay * 2, 120_000);
			} finally {
				// A paused round can leave pages unread; it must not consume recovery attempts.
				if (canPoll()) {
					// One result per round, including errors; pagination must not consume retries.
					for (const [start, receive] of receivers) {
						if (listeners.has(start)) receive(snapshot, failureReason);
					}
				}
				inFlight = false;
				if (listeners.size) schedule();
				else pages?.delete(pageId);
			}
		};
		poll = {
			listeners,
			resume: () => {
				browserDocument.addEventListener('visibilitychange', schedule);
				window.addEventListener('online', schedule);
				window.addEventListener('offline', schedule);
				schedule();
			},
			release: () => {
				clearTimer();
				browserDocument.removeEventListener('visibilitychange', schedule);
				window.removeEventListener('online', schedule);
				window.removeEventListener('offline', schedule);
				// Retain a pending request so a quick close/reopen cannot overlap it.
				if (!inFlight) pages?.delete(pageId);
			},
		};
		pages.set(pageId, poll);
	}
	const wasEmpty = poll.listeners.size === 0;
	poll.listeners.set(listener, (poll.listeners.get(listener) ?? 0) + 1);
	if (wasEmpty) poll.resume();
	let released = false;
	return () => {
		if (released) return;
		released = true;
		const remaining = (poll.listeners.get(listener) ?? 1) - 1;
		if (remaining) poll.listeners.set(listener, remaining);
		else poll.listeners.delete(listener);
		if (!poll.listeners.size) poll.release();
	};
}
